import tinyColor from "tinycolor2";
import { easeInOutQuart } from "js-easing-functions";

const easing = {
  easeInOutQuart,
};

const sweeper = {
  blanking: 20,
  dotLength: 7,
  clip({ state }) {
    const ctx = state.ctx;
    const canvas = ctx.canvas;
    const now = performance.now();
    if (state.renderRangeX && state.sweeper.cumulativeRenderRangeX) {
      const msSinceStart = now - state.sweeperStarted;
      const pxDistanceSinceStart =
        (msSinceStart / 1000) * state.pixelsPerSecond + canvas.width;

      let renderRangeEnd = pxDistanceSinceStart;
      // If you didn't have enough data last time, keep the left edge where it is. This is the back-fill fix.
      let renderRangeStart = state.sweeper.cumulativeRenderRangeX[1];
      const liveChartTime = state.getLiveChartTime(state.renderTime);
      if (liveChartTime > state.dataEndDateTime) {
        // Were going to stall advancing the tail end to just before the last point of data.
        const dataMs =
          state.canvasPoints.dataEndDateTime - state.sweeper.chartStartDateTime;
        const pxDistanceOfData = (dataMs / 1000) * state.pixelsPerSecond;
        renderRangeStart = pxDistanceOfData;
        if (renderRangeStart > renderRangeEnd) {
          renderRangeStart = state.sweeper.cumulativeRenderRangeX[0];
        }

        // In case the last data has stalled long enough for the monitor to wrap all the way around,
        // At this point, we'll halt and wait for the data to catch up.
        if (
          renderRangeEnd - renderRangeStart + sweeper.blanking >
          canvas.width
        ) {
          return { error: "out of data" };
        }
      }
      renderRangeStart = Math.max(renderRangeStart, 0);

      // This should never happen, if it does there was a problem with locating the stall position.
      if (renderRangeStart > renderRangeEnd) {
        console.log(
          "renderRangeStart > renderRangeEnd",
          renderRangeStart,
          renderRangeEnd,
        );
        return { error: "render range inverted" };
      }

      state.sweeper.cumulativeRenderRangeX = [renderRangeStart, renderRangeEnd];
      state.renderRangeX = sweeper.renderRangeToCoords(state);
      sweeper.clearHead(state);

      let region = new Path2D();
      if (state.renderRangeX[0] < state.renderRangeX[1]) {
        region.rect(
          state.renderRangeX[0],
          0,
          state.renderRangeX[1] - state.renderRangeX[0],
          canvas.height,
        );
      } else {
        region.rect(state.renderRangeX[0], 0, canvas.width, canvas.height);
        region.rect(0, 0, state.renderRangeX[1], canvas.height);
      }

      // debug Show render regions.
      // ctx.fillStyle = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      // ctx.fill(region);

      ctx.clip(region);
    } else {
      state.renderRangeX = [0, ctx.canvas.width];
      state.sweeper.cumulativeRenderRangeX = [0, ctx.canvas.width];
      sweeper.clearHead(state);
      state.sweeperStarted = now;
    }

    const renderRangeStart = state.sweeper.cumulativeRenderRangeX[0];
    const pageNumber = Math.max(
      0,
      Math.floor((renderRangeStart - sweeper.leftOverscan) / canvas.width),
    );
    const wrapped = pageNumber > state.sweeper.pageNumber;
    state.sweeper.pageNumber = pageNumber;

    state.renderTime = now;
    return {
      isWrapping: state.renderRangeX[0] > state.renderRangeX[1],
      wrapped,
    };
  },
  get leftOverscan() {
    return sweeper.dotLength + 2;
  },
  renderRangeToCoords(state) {
    let [start, end] = state.sweeper.cumulativeRenderRangeX;
    start = start - sweeper.leftOverscan;
    return [start, end].map(state.sweeper.modX).map(Math.floor);
  },
  clearHead(state) {
    const { renderRangeX } = state;
    if (renderRangeX[0] < renderRangeX[1]) {
      state.ctx.clearRect(
        renderRangeX[0],
        0,
        renderRangeX[1] - renderRangeX[0] + sweeper.blanking,
        state.ctx.canvas.height,
      );
    } else {
      state.ctx.clearRect(
        renderRangeX[0],
        0,
        state.ctx.canvas.width,
        state.ctx.canvas.height,
      );
      state.ctx.clearRect(
        0,
        0,
        renderRangeX[1] + sweeper.blanking,
        state.ctx.canvas.height,
      );
    }
  },
  reset(state) {
    state.sweeper = {
      page: 0,
    };
    clearTimeout(state.coolDown);
    state.coolDown = undefined;
    state.renderTime = undefined;
    state.renderRangeX = undefined;
    state.canvasPoints = undefined;
    state.nextCanvasPoints = undefined;
  },
  getModuloFunction: function (state) {
    const { width } = state.canvas;
    if (state.sweeper.modX?.width !== width) {
      state.sweeper.modX = (x) => (x + width) % width;
      state.sweeper.modX.width = width;
    }
    return state.sweeper.modX;
  },
  paintCanvas({ state, now, renderPoint }) {
    if (state.coolDown) return;
    if (!state.canvasPoints?.chartEndDateTime) return;
    if (!state.sweeper.chartStartDateTime) {
      state.sweeper.chartStartDateTime =
        state.canvasPoints?.chartEndDateTime.minus({
          seconds: state.canvasWidthSeconds,
        });
    }
    if (state.rescaled) {
      state.rescaled = false;
    }
    const { canvas, ctx } = state;
    sweeper.getModuloFunction(state);
    ctx.save();
    const { isWrapping, error, wrapped } = sweeper.clip({ state });
    if (error) {
      console.error("sweeper.clip() error:", error);
      state.clearCanvas();
      sweeper.reset(state);
      if (error === "out of data") {
        state.errorCount = state.errorCount + 1 || 1;
        console.error(
          "pausing sweep-mode rendering for",
          state.errorCount,
          "seconds",
        );
        clearTimeout(state.coolDown);
        state.coolDown = setTimeout(() => {
          state.coolDown = null;
        }, state.errorCount * 1000);
      }
      return;
    }

    if (wrapped) {
      state.prevCanvasPoints = state.canvasPoints;
      state.canvasPoints = state.nextCanvasPoints;
      state.nextCanvasPoints = undefined;
    }

    state.waveGroups.forEach((group) => {
      if (!state.canvasPoints?.[group.key]) {
        return ctx.restore();
      }

      const lineColor = state.lineColor || group.lineColor;
      ctx.strokeStyle = group.disabled
        ? tinyColor(lineColor).setAlpha(0.4)
        : lineColor;
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.beginPath();

      if (isWrapping) {
        [
          ...slicePoints({
            points: state.nextCanvasPoints?.[group.key]?.points,
            xMin: 0,
            xMax: state.renderRangeX[1],
            lineWidth: ctx.lineWidth,
          }),
          null,
        ].forEach(renderPoint);
      }

      slicePoints({
        points: state.canvasPoints[group.key].points,
        xMin: state.renderRangeX[0],
        xMax: isWrapping ? canvas.width : state.renderRangeX[1],
        lineWidth: ctx.lineWidth,
      }).forEach(renderPoint);

      state.canvasPoints[group.key].renderTime = state.liveMode
        ? now
        : undefined;
      ctx.stroke();
      //highlight head
      if (!state.sweepHideDot)
        this.highlightHead({ lineColor, group, state, renderPoint });
    });

    ctx.restore();
  },
  highlightHead({ lineColor, group, state, renderPoint }) {
    const ctx = state.ctx;
    const canvas = state.canvas;
    const xMin = state.renderRangeX[1] - sweeper.dotLength;
    const xMax = state.renderRangeX[1];
    const region = new Path2D();
    region.rect(xMin, 0, sweeper.dotLength, canvas.height);
    ctx.save();
    // TODO: memoize of cache this color
    ctx.strokeStyle = tinyColor(lineColor).brighten(50);
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.clip(region);
    ctx.beginPath();
    slicePoints({
      points: state.canvasPoints[group.key].points,
      xMin,
      xMax,
      lineWidth: ctx.lineWidth,
    }).forEach(renderPoint);
    ctx.stroke();
    ctx.restore();
  },
  resetOnTilt({ state, options }) {
    if (
      state.isSweeping &&
      typeof state.offsetY === "number" &&
      state.offsetY !== options.offsetY
    ) {
      sweeper.reset(state);
      state.rescaled = true;
    }
    state.offsetY = options.offsetY;
  },
  wrapCanvasPoints({ state, canvasPoints: newCanvasPoints }) {
    if (!newCanvasPoints?.dataEndDateTime) return;
    state.dataEndDateTime = newCanvasPoints.dataEndDateTime;
    let oldCanvasPoints = state.canvasPoints;
    const width = state.canvas.width;
    if (oldCanvasPoints) {
      if (!oldCanvasPoints.filled) {
        const diffMs = newCanvasPoints.chartEndDateTime.diff(
          oldCanvasPoints.chartEndDateTime,
        );
        let correctedNewCanvasPoints = newCanvasPoints;
        if (Math.abs(diffMs) > 10) {
          const xOffset = (state.pixelsPerSecond * diffMs) / 1000;
          console.warn("time correct. ms:", +diffMs, "px", xOffset);
          correctedNewCanvasPoints.chartEndDateTime =
            oldCanvasPoints.chartEndDateTime;
          correctedNewCanvasPoints = newCanvasPoints.translate({ xOffset });
        }
        state.canvasPoints = correctedNewCanvasPoints.prepend(oldCanvasPoints);
      }
    } else {
      state.canvasPoints = newCanvasPoints;
    }

    if (state.nextCanvasPoints) {
      const diffMs = newCanvasPoints.chartEndDateTime.diff(
        state.nextCanvasPoints.chartEndDateTime,
      );
      if (Math.abs(diffMs) < 10) {
        state.nextCanvasPoints = newCanvasPoints.prepend(
          state.nextCanvasPoints,
        );
      } else {
        const xOffset = (state.pixelsPerSecond * diffMs) / 1000;
        let correctedNewCanvasPoints = newCanvasPoints.translate({ xOffset });
        correctedNewCanvasPoints.chartEndDateTime =
          state.nextCanvasPoints.chartEndDateTime;
        state.nextCanvasPoints = correctedNewCanvasPoints.prepend(
          state.nextCanvasPoints,
        );
      }
    } else {
      if (state.canvasPoints.filled) {
        const nextCanvasPoints = state.canvasPoints
          .translate({ xOffset: -width })
          .slicePoints({ xMin: 0 });
        nextCanvasPoints.chartEndDateTime =
          state.canvasPoints.chartEndDateTime.plus({
            seconds: state.canvasWidthSeconds,
          });
        state.nextCanvasPoints = nextCanvasPoints;
      }
    }
  },
  async transitionOut(state) {
    const headPosition = state.renderRangeX?.[1];
    if (!headPosition) return;
    state.ctx.restore();
    const distance = state.canvas.width - headPosition;

    sweeper.reset(state);
    const imageData = sweeper.getUnwrappedImageData({
      state,
      headPosition,
    });
    state.transition = {
      imageData,
      startTimeMs: performance.now(),
      durationMs:
        (distance / state.canvas.width) * state.transitionConfig.maxDurationMs,
      easingFunction: easing[state.transitionConfig.easingFunction],
      startPosition: -distance,
      distance,
    };

    return new Promise((resolve) => {
      sweeper.animateTransition({ state, resolve });
    });
  },

  getUnwrappedImageData({ state, headPosition }) {
    const latestImageData = state.ctx.getImageData(
      0,
      0,
      headPosition,
      state.canvas.height,
    );
    const prevImageData = state.ctx.getImageData(
      headPosition,
      0,
      state.canvas.width,
      state.canvas.height,
    );
    const canvas = new OffscreenCanvas(state.canvas.width, state.canvas.height),
      ctx = canvas.getContext("2d");
    ctx.putImageData(prevImageData, 0, 0);
    ctx.putImageData(latestImageData, canvas.width - headPosition, 0);
    const imageData = ctx.getImageData(
      0,
      0,
      state.canvas.width,
      state.canvas.height,
    );
    return imageData;
  },
  animateTransition({ state, resolve }) {
    const elapsed = performance.now() - state.transition.startTimeMs;
    let x = 0;
    if (elapsed < state.transition.durationMs) {
      requestAnimationFrame(() => {
        sweeper.animateTransition({
          state,
          resolve,
        });
      });
      x = state.transition.easingFunction(
        elapsed,
        state.transition.startPosition,
        state.transition.distance,
        state.transition.durationMs,
      );
    }

    state.ctx.clearRect(0, 0, state.canvas.width, state.canvas.height);
    state.ctx.putImageData(state.transition.imageData, x, 0);

    if (elapsed >= state.transition.durationMs) {
      state.transition = undefined;
      resolve(true);
    }
  },
};

export default sweeper;

/**
 * returns an inclusive range of points includes the points just oustide the range.
 * @param {array} points
 * @param xMin
 * @param xMax
 * @param lineWidth - additional buffer to include points outside the range.
 * @returns {array}
 */
export function slicePoints({
  points,
  xMin,
  xMax,
  lineWidth = 0,
  lastIndexOffset = 2,
}) {
  if (!points) return [];
  let firstIndex = 0;
  let lastIndex = points.length;
  if (!isNaN(xMin)) {
    firstIndex = points.findIndex((p) => p && p[0] >= xMin - lineWidth);
    if (firstIndex === -1) {
      return [];
    }
  }
  if (!isNaN(xMax)) {
    lastIndex = points.findLastIndex((p) => p && p[0] <= xMax + lineWidth);
    if (lastIndex === -1) {
      return [];
    }
  }
  firstIndex = Math.max(0, firstIndex - 1);
  // we have to add 1 for the last point inside and another 1 for the point after.
  lastIndex = lastIndex + lastIndexOffset;
  return points.slice(firstIndex, lastIndex);
}
